import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import cx from 'classnames';
import { DateTime } from 'luxon';

import withStyles from '@mui/styles/withStyles';
import Tooltip from '@mui/material/Tooltip';

import { replaceTask } from '../store/slices/taskInfoSlice';
import {
  setCaseIntendedClientId,
  setIntendedInquiryId,
  setIntendedReferrerCompanyId,
  setIntendedReferrerContactId,
} from '../store/slices/intendedInfoSlice';

import { createStdRequest, apiFetch, dateStringForDateTime, showAlert } from '../utils/utils';

import DA from '../store/ReduxAccess';

import taskAssocGroupStyles from '../assets/jss/crm/components/taskAssocGroupStyles';

import TaskModal from './TaskModal';
import AlertModal from './AlertModal';

class TaskSummary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showTaskModal: false,
      taskToView: props.task,
    };
  }

  tooltipTextForTask(task) {
    switch (task.currentState) {
      case 'Unassigned':
      case 'Assigned':
        return 'Accept Task';
      case 'Claimed':
        return 'Mark Completed';
      case 'Completed':
      case 'Canceled':
        return 'Reopen Task';
      default:
        return 'Accept Task';
    }
  }

  performDefaultActionOnTask(task) {
    switch (task.currentState) {
      case 'Unassigned':
      case 'Assigned':
        this.acceptTask(task);
        break;
      case 'Claimed':
        this.markTaskAsCompleted(task);
        break;
      case 'Completed':
      case 'Canceled':
        this.reopenTask(task);
        break;
      default:
        console.log("Don't know what to do with task in state: " + task.currentState);
        break;
    }
  }

  changeTaskState(task, newState) {
    const apiStruct = {
      newState: newState,
      userEmail: this.props.userInfo.email,
    };
    const uri = `/api/task/${task.id}/setState`;
    const request = createStdRequest(uri, 'PUT', apiStruct);
    const actionOnGoodResponse = (json) => {
      const success = json.success;
      if (success) {
        const newTaskRep = json.task; // TaskInfoTaskItem
        this.props.replaceTask(newTaskRep);
      }
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  acceptTask(task) {
    this.changeTaskState(task, 'Claimed');
  }

  markTaskAsCompleted(task) {
    this.changeTaskState(task, 'Completed');
  }

  reopenTask(task) {
    if (task.claimantId) {
      this.changeTaskState(task, 'Claimed');
    } else if (task.assigneeId) {
      this.changeTaskState(task, 'Assigned');
    } else {
      this.changeTaskState(task, 'Unassigned');
    }
  }

  showModalForTask(task) {
    this.setState({
      taskToView: task,
      showTaskModal: true,
    });
  }

  closeTaskModal() {
    this.setState({
      showTaskModal: false,
    });
  }

  handleRedirectToSubject = (task) => {
    const subjectId = task.subjectId;
    const subjectFirmId = task.subjectFirmId;
    const inquiryId = task.inquiryId;

    // For Inquiry
    if (inquiryId > 0) {
      const inquiry = DA.getInquiryById(inquiryId);
      if (inquiry) {
        this.props.setIntendedInquiryId(inquiry.inquiryId);
        this.setState({ redirect: 'lead' });
      } else {
        showAlert(this, `there is no inquiry with ${inquiryId} as a contact`);
      }
    }

    // For Prospect/Active Client/Prospect
    if (subjectId > 0 && subjectFirmId === 0) {
      const id = subjectId;
      const person = DA.getPersonById(id);
      if (person) {
        this.props.setCaseIntendedClientId(person.personId);
        this.setState({ redirect: 'case' });
      } else {
        const clientIds = DA.getIdsOfClientsWithEmergencyContactWithId(id);
        if (clientIds.length > 0) {
          const clientId = clientIds[0];
          this.props.setCaseIntendedClientId(clientId);
          this.setState({ redirect: 'case' });
        } else {
          showAlert(this, `there is no client with person ${id} as a contact`);
        }
      }
    }

    // For Referral Company
    if (subjectFirmId > 0 && subjectId === 0) {
      const firm = DA.getFirmById(subjectFirmId);
      if (firm) {
        this.props.setIntendedReferrerCompanyId(firm.firmId);
        this.setState({ redirect: 'referrerOrg' });
      } else {
        showAlert(this, `there is no firm with ${subjectFirmId} as a contact`);
      }
    }

    // For Referral Contact
    if (subjectFirmId > 0 && subjectId > 0) {
      const person = DA.getPersonById(subjectId);
      const firm = DA.getFirmById(subjectFirmId);
      if (person && firm) {
        this.props.setIntendedReferrerCompanyId(firm.firmId);
        this.props.setIntendedReferrerContactId(person.personId);
        this.setState({ redirect: 'referrerContact' });
      } else {
        showAlert(this, `there is no contact with person ${subjectId} as a contact`);
      }
    }
  };

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} push />;
    }

    const { classes, task } = this.props;

    const nextDeadline = task.nextDeadline;
    const now = DateTime.local();
    const daysUntilDue = nextDeadline.diff(now).as('days');
    const isCompleted = task.currentState === 'Completed';
    const isCanceled = task.currentState === 'Canceled';
    const isOverdue = !(isCompleted || isCanceled) && daysUntilDue < 0;
    const isUrgent = !(isCompleted || isCanceled || isOverdue) && daysUntilDue <= 2;
    const isNotUrgent = !(isCompleted || isCanceled || isOverdue || isUrgent);
    const rootClasses = cx(classes.entitySummary, classes.entitySummary_task, { isComplete: isCompleted });
    const colorClass = cx({
      isOverdue: isOverdue,
      isUrgent: isUrgent,
      isNotUrgent: isNotUrgent,
      isCompleted: isCompleted,
      isCanceled: isCanceled,
    });
    const tooltipText = this.tooltipTextForTask(task);
    const posessorLabel = task.currentState === 'Unassigned' ? ' Owner: ' : ' Assigned to: ';
    const haveAssignee = task.assigneeId;
    const haveClaimant = task.claimantId;
    const posessorName =
      task.currentState === 'Unassigned'
        ? DA.getPersonById(task.ownerId).preferredFullName
        : haveAssignee
        ? DA.getPersonById(task.assigneeId).preferredFullName
        : haveClaimant
        ? DA.getPersonById(task.claimantId).preferredFullName
        : task.assignedGroupId > 0
        ? DA.getTaskableGroupById(task.assignedGroupId).name
        : 'None';

    const subjectName =
      task.subjectId > 0
        ? DA.getPersonById(task.subjectId).preferredFullName
        : task.inquiryId > 0
        ? DA.getInquiryById(task.inquiryId).name
        : task.subjectFirmId > 0
        ? DA.getFirmById(task.subjectFirmId).name
        : null;

    return (
      <div>
        <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />

        <TaskModal
          show={this.state.showTaskModal}
          task={this.state.taskToView}
          groups={this.props.taskInfo.taskableGroups}
          onClose={() => this.closeTaskModal()}
        />

        <div className={rootClasses}>
          <Tooltip title={tooltipText} placement="top">
            <div className={cx(classes.entitySummary_icon, classes.taskCheckBox_small, classes.taskCheckBox)}>
              <div className={cx(classes.taskCheckBox_container, colorClass)}>
                <div className={classes.taskCheckBox_checkbox}>
                  <i
                    className={classes.u_cursorPointer + ' zmdi zmdi-check-square'}
                    onClick={() => this.performDefaultActionOnTask(task)}
                  />
                </div>
                <div className={classes.taskCheckBox_activityIcon}>
                  <i className={classes.u_cursorPointer + ' zmdi zmdi-check-circle'} />
                </div>
              </div>
            </div>
          </Tooltip>
          <div className={cx(classes.entitySummary_detail, classes.ac_h5, classes.u_ellipsis)}>
            {dateStringForDateTime(task.completionDeadline)}
          </div>
          <div
            className={cx(classes.entitySummary_title, classes.u_cursorPointer, classes.u_ellipsis, classes.ac_h4)}
            onClick={() => this.showModalForTask(task)}
          >
            {task.description}
          </div>
          <div className={cx(classes.entitySummary_subtitle, classes.u_ellipsis, classes.ac_h5)}>
            {subjectName ? (
              <div>
                <div
                  onClick={() => this.handleRedirectToSubject(task)}
                  className={classes.entitySummary_subtitleName + ' ' + classes.ac_a}
                >
                  {subjectName}
                </div>
                <span>{' / '}</span>
              </div>
            ) : null}
            <span className={classes.entitySummary_subtitleName}>
              {posessorLabel}
              {posessorName}
            </span>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
  taskInfo: state.taskInfo,
  firmInfo: state.firmInfo,
});
const actionProducers = {
  replaceTask,
  setCaseIntendedClientId,
  setIntendedInquiryId,
  setIntendedReferrerCompanyId,
  setIntendedReferrerContactId,
};
export default connect(mapStateToProps, actionProducers)(withStyles(taskAssocGroupStyles)(TaskSummary));
